import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMenu } from '../contexts/MenuContext';
import { addDoc, collection, query, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { toast } from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { ShoppingCart, MapPin, Clock, CheckCircle, AlertCircle, Plus, Minus, Trash2, CreditCard } from 'lucide-react';
import GoogleMap from '../components/GoogleMap';
import { useOpeningHours } from '../contexts/OpeningHoursContext';
import { formatOpeningHours } from '../utils/openingHours';
import StripeCheckout from '../components/StripeCheckout';

const schema = yup.object({
  notes: yup.string()
});

type OrderFormData = yup.InferType<typeof schema>;

const OrderPage: React.FC = () => {
  const { cart, getCartTotal, clearCart, updateCartItem, removeFromCart } = useMenu();
  const { openingHours, loading } = useOpeningHours();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderNumber, setOrderNumber] = useState<number | null>(null);
  const [orderingEnabled, setOrderingEnabled] = useState(true);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [pendingOrderData, setPendingOrderData] = useState<any>(null);
  const [paymentMethod, setPaymentMethod] = useState<'stripe' | 'counter'>('counter');
  const [completedOrderItems, setCompletedOrderItems] = useState<any[]>([]);
  const [serviceType, setServiceType] = useState<'takeaway' | 'dine-in' | ''>('');
  const [completedOrderTotal, setCompletedOrderTotal] = useState<number>(0);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      notes: ''
    }
  });

  // Check if ordering is enabled
  React.useEffect(() => {
    const checkOrderingStatus = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'settings', 'ordering'));
        if (settingsDoc.exists()) {
          setOrderingEnabled(settingsDoc.data().enabled ?? true);
        }
      } catch (error) {
        console.error('Error checking ordering status:', error);
        // Default to enabled if there's an error
        setOrderingEnabled(true);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkOrderingStatus();
  }, []);

  const handleCounterPayment = async (data: OrderFormData) => {
    setIsSubmitting(true);
    try {
      console.log('💰 Processing counter payment order...');

      // Get the latest order number from Firestore
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, orderBy('orderNumber', 'desc'), limit(1));
      const querySnapshot = await getDocs(q);
      
      let generatedOrderNumber = 1;
      if (!querySnapshot.empty) {
        const lastOrder = querySnapshot.docs[0].data();
        const lastOrderNumber = lastOrder.orderNumber || 0;
        
        if (lastOrderNumber > 10000) {
          generatedOrderNumber = 1;
        } else {
          generatedOrderNumber = lastOrderNumber + 1;
        }
      }

      // Serialize cart items
      const serializedItems = JSON.parse(JSON.stringify(cart.map(item => ({
        id: item.id,
        quantity: item.quantity,
        notes: item.notes || '',
        menuItem: {
          id: item.menuItem.id,
          name: item.menuItem.name,
          price: Number(item.menuItem.price),
          categoryId: item.menuItem.categoryId,
          description: item.menuItem.description || '',
          imageUrl: item.menuItem.imageUrl || ''
        },
        selectedExtras: (item.selectedExtras || []).map(extra => ({
          quantity: extra.quantity,
          extra: {
            id: extra.extra.id,
            name: extra.extra.name,
            price: Number(extra.extra.price)
          }
        }))
      }))));

      // Create order with "Pay at Counter" flag
      const orderData = {
        orderNumber: generatedOrderNumber,
        serviceType,
        notes: String(data.notes || ''),
        items: serializedItems,
        total: Number(getCartTotal().toFixed(2)),
        status: 'pending',
        paymentMethod: 'counter',
        paymentStatus: 'pending',
        estimatedTime: '15-20 minutes',
        createdAt: new Date()
      };

      // Save order directly to Firestore
      await addDoc(collection(db, 'orders'), orderData);

      console.log('✅ Counter payment order created:', generatedOrderNumber);
      
      // Save order details for confirmation screen
      setCompletedOrderItems([...cart]);
      setCompletedOrderTotal(getCartTotal());
      
      // Clear cart and show success
      clearCart();
      setOrderNumber(generatedOrderNumber);
      setOrderSuccess(true);
      toast.success('Order placed successfully!');
    } catch (error: any) {
      console.error('❌ Error creating counter payment order:', error);
      toast.error(`Failed to place order: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = async (data: OrderFormData) => {
    if (!serviceType) {
      toast.error('Please select Takeaway or Dine In before placing your order');
      return;
    }
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    // If Pay at Counter is selected, handle it differently
    if (paymentMethod === 'counter') {
      return handleCounterPayment(data);
    }

    try {
      console.log('📦 Preparing order for payment...', {
        cartItems: cart.length
      });

      // Get the latest order number from Firestore
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, orderBy('orderNumber', 'desc'), limit(1));
      const querySnapshot = await getDocs(q);
      
      let generatedOrderNumber = 1; // Start from 1
      if (!querySnapshot.empty) {
        const lastOrder = querySnapshot.docs[0].data();
        const lastOrderNumber = lastOrder.orderNumber || 0;
        
        // If last order number is abnormally high (old timestamp-based), reset to 1
        // Otherwise, increment normally
        if (lastOrderNumber > 10000) {
          generatedOrderNumber = 1;
        } else {
          generatedOrderNumber = lastOrderNumber + 1;
        }
      }

      // Serialize cart items to ensure compatibility with Firestore and Safari
      const serializedItems = JSON.parse(JSON.stringify(cart.map(item => ({
        id: item.id,
        quantity: item.quantity,
        notes: item.notes || '',
        menuItem: {
          id: item.menuItem.id,
          name: item.menuItem.name,
          price: Number(item.menuItem.price),
          categoryId: item.menuItem.categoryId,
          description: item.menuItem.description || '',
          imageUrl: item.menuItem.imageUrl || ''
        },
        selectedExtras: (item.selectedExtras || []).map(extra => ({
          quantity: extra.quantity,
          extra: {
            id: extra.extra.id,
            name: extra.extra.name,
            price: Number(extra.extra.price)
          }
        }))
      }))));

      const orderData = {
        orderNumber: generatedOrderNumber,
        serviceType,
        notes: String(data.notes || ''),
        items: serializedItems,
        total: Number(getCartTotal().toFixed(2)),
        status: 'pending',
        estimatedTime: '15-20 minutes',
        paymentMethod: 'stripe'
      };

      // Store order data in sessionStorage for retrieval after Stripe redirect
      sessionStorage.setItem('pendingOrder', JSON.stringify(orderData));
      
      // Directly redirect to Stripe checkout (skip the modal)
      console.log('🚀 Starting payment process...');
      console.log('📦 Order items:', serializedItems);
      console.log('💰 Order total:', getCartTotal());
      console.log('🌐 Calling URL:', '/api/create-checkout-session');
      console.log('🌐 Full URL will be:', window.location.origin + '/api/create-checkout-session');

      // Primary API via Hosting rewrite with fallback to direct Cloud Functions URL
      const primaryUrl = '/api/create-checkout-session';
      const fallbackUrl = 'https://us-central1-kisscoffee-3960a.cloudfunctions.net/api/create-checkout-session';

      const makeRequest = async (url: string) => {
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          mode: 'cors',
          body: JSON.stringify({
            orderItems: serializedItems,
            orderTotal: Math.round(getCartTotal() * 100),
            successUrl: `${window.location.origin}/order-success?session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: `${window.location.origin}/order`,
          }),
        });

        const text = await res.text();
        const contentType = res.headers.get('content-type') || '';
        console.log('📡 Response status:', res.status);
        console.log('📡 Response ok:', res.ok);
        console.log('📡 Response headers:', contentType);
        console.log('📄 Raw response (first 200 chars):', text.substring(0, 200));

        // Treat as JSON only if content-type indicates JSON and parsing succeeds
        if (!contentType.includes('application/json')) {
          throw new Error('Non-JSON response');
        }

        let parsed: any;
        try {
          parsed = JSON.parse(text);
        } catch {
          throw new Error('JSON parse failed');
        }

        if (!res.ok || !parsed.url) {
          const errMsg = (parsed && (parsed.error || parsed.details)) || 'Failed to create checkout session';
          throw new Error(errMsg);
        }

        return parsed;
      };

      let result;
      try {
        console.log('🌐 Trying primary API via Hosting rewrite...');
        result = await makeRequest(primaryUrl);
      } catch (primaryErr) {
        console.warn('⚠️ Primary API failed, falling back to direct CF URL:', primaryErr);
        result = await makeRequest(fallbackUrl);
      }

      console.log('✅ Redirecting to Stripe:', result.url);
      window.location.href = result.url;

    } catch (error: any) {
      console.error('❌ Error preparing order:', error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error stack:', error.stack);
      toast.error(`Failed to start payment: ${error.message}`);
    }
  };

  // Payment success is now handled by OrderSuccess page after Stripe redirect

  // Show loading state while checking ordering status
  if (checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="relative w-12 h-12 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-red-200 border-t-red-500 rounded-full animate-spin"></div>
            <div className="absolute inset-1 bg-black rounded-full flex items-center justify-center overflow-hidden">
              <img src="/logo1.png" alt="Logo" className="h-8 w-8 object-contain" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-700 mb-2">Loading...</h1>
          <p className="text-gray-500">Preparing your order form</p>
        </div>
      </div>
    );
  }

  // Show disabled message if ordering is disabled
  if (!checkingStatus && !orderingEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-700 mb-2">Online Ordering Unavailable</h1>
          <p className="text-gray-600 mb-6">
            We're not accepting online orders at the moment. Please visit us in-store or call us directly.
          </p>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">Visit us at:</p>
            <p className="font-medium">16 Friday Street, Minehead, TA24 5UA</p>
            <button 
              onClick={() => navigate('/')}
              className="btn btn-primary"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (cart.length === 0 && !orderSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <ShoppingCart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-700 mb-2">Your cart is empty</h1>
          <p className="text-gray-500 mb-6">Add some delicious items to your cart first!</p>
          <button 
            onClick={() => navigate('/menu')}
            className="btn btn-primary"
          >
            Browse Menu
          </button>
        </div>
      </div>
    );
  }

  if (orderSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-lg mx-auto p-8"
        >
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Order Confirmed!</h1>
          <p className="text-gray-600 mb-6">
            Thank you for your order! We'll prepare it and notify you when it's ready.
          </p>
          <div className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <h3 className="text-2xl font-bold text-red-600 mb-4">Order #{orderNumber}</h3>
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3 text-left">Order Details</h4>
              {completedOrderItems.length > 0 && (
                <div className="space-y-2 mb-4">
                  {completedOrderItems.map((item, index) => (
                    <div key={index} className="flex justify-between items-start text-sm text-left">
                      <div className="flex-1">
                        <p className="font-medium">{item.quantity}x {item.menuItem.name}</p>
                        {item.selectedExtras && item.selectedExtras.length > 0 && (
                          <p className="text-xs text-gray-500 ml-4">
                            {item.selectedExtras.map((e: any) => `+${e.quantity}x ${e.extra.name}`).join(', ')}
                          </p>
                        )}
                      </div>
                      <span className="font-medium ml-3">
                        £{(item.menuItem.price * item.quantity + 
                           (item.selectedExtras?.reduce((sum: number, e: any) => sum + (e.extra.price * e.quantity), 0) || 0)
                        ).toFixed(2)}
                      </span>
                    </div>
                  ))}
                  <div className="border-t pt-2 mt-3 flex justify-between font-bold">
                    <span>Total:</span>
                    <span>£{completedOrderTotal.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <button 
            onClick={() => navigate('/')}
            className="btn btn-primary"
          >
            Back to Home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Order Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-lg shadow-sm p-6"
            >
              <h2 className="text-lg font-semibold mb-4">Place Your Order</h2>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* Service Type Selection (moved to very top) */}
                <div className="flex gap-3">
                  <label className={`flex-1 flex items-center justify-center cursor-pointer px-3 py-2.5 border-2 rounded-lg transition-all ${
                    serviceType === 'takeaway' 
                      ? 'border-red-600 bg-red-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="serviceType"
                      value="takeaway"
                      checked={serviceType === 'takeaway'}
                      onChange={() => setServiceType('takeaway')}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium">Takeaway</span>
                  </label>
                  <label className={`flex-1 flex items-center justify-center cursor-pointer px-3 py-2.5 border-2 rounded-lg transition-all ${
                    serviceType === 'dine-in' 
                      ? 'border-red-600 bg-red-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="serviceType"
                      value="dine-in"
                      checked={serviceType === 'dine-in'}
                      onChange={() => setServiceType('dine-in')}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium">Dine In</span>
                  </label>
                </div>

                {/* Payment Method Selection - No heading */}
                <div className="flex gap-3">
                  <label
                    onClick={() => toast.error('Online payments are currently unavailable. Please pay at the counter.')}
                    className={`flex-1 flex items-center justify-center cursor-not-allowed px-3 py-2.5 border-2 rounded-lg transition-all border-gray-200 bg-gray-50 opacity-70`}
                    title="Online payments unavailable"
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="stripe"
                      checked={false}
                      readOnly
                      className="sr-only"
                    />
                    <CreditCard className="h-4 w-4 mr-2 text-gray-600" />
                    <span className="text-sm font-medium">Pay Online (Unavailable)</span>
                  </label>
                  <label className={`flex-1 flex items-center justify-center cursor-pointer px-3 py-2.5 border-2 rounded-lg transition-all ${
                    paymentMethod === 'counter' 
                      ? 'border-red-600 bg-red-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="counter"
                      checked={paymentMethod === 'counter'}
                      onChange={() => setPaymentMethod('counter')}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium">💵 Pay at Counter</span>
                  </label>
                </div>

                {/* Special Instructions - Auto-expanding textarea */}
                <div>
                  <textarea
                    {...register('notes')}
                    className="form-input form-textarea resize-none overflow-hidden"
                    placeholder="Special instructions (optional)"
                    rows={1}
                    onInput={(e) => {
                      const target = e.target as HTMLTextAreaElement;
                      target.style.height = 'auto';
                      target.style.height = target.scrollHeight + 'px';
                    }}
                  />
                </div>

                {/* Order Summary */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
                  <div className="space-y-3">
                    {cart.map((item) => (
                      <div key={item.id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{item.menuItem.name}</p>
                          {item.selectedExtras.length > 0 && (
                            <p className="text-xs text-gray-500 truncate">
                              +{item.selectedExtras.length} extras
                            </p>
                          )}
                          {item.notes && (
                            <p className="text-xs text-gray-500 truncate">Note: {item.notes}</p>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {/* Quantity Controls */}
                          <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-300">
                            <button
                              type="button"
                              onClick={() => updateCartItem(item.id, item.quantity - 1, item.selectedExtras, item.notes)}
                              className="p-1 hover:bg-gray-100 rounded-l-lg transition-colors"
                              aria-label="Decrease quantity"
                            >
                              <Minus className="h-3 w-3 text-gray-600" />
                            </button>
                            <span className="px-2 text-sm font-medium min-w-[24px] text-center">
                              {item.quantity}
                            </span>
                            <button
                              type="button"
                              onClick={() => updateCartItem(item.id, item.quantity + 1, item.selectedExtras, item.notes)}
                              className="p-1 hover:bg-gray-100 rounded-r-lg transition-colors"
                              aria-label="Increase quantity"
                            >
                              <Plus className="h-3 w-3 text-gray-600" />
                            </button>
                          </div>
                          
                          {/* Price */}
                          <span className="text-sm font-semibold min-w-[60px] text-right">
                            £{(item.menuItem.price * item.quantity).toFixed(2)}
                          </span>
                          
                          {/* Remove Button */}
                          <button
                            type="button"
                            onClick={() => removeFromCart(item.id)}
                            className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            aria-label="Remove item"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-4 mt-4">
                    <div className="flex justify-between text-lg font-semibold">
                      <span>Total:</span>
                      <span className="text-gray-800">£{getCartTotal().toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={() => navigate('/menu')}
                    className="btn btn-outline btn-sm px-6 py-2 flex-1"
                  >
                    Back to Menu
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn btn-primary btn-sm px-6 py-2 flex-1 flex items-center justify-center"
                  >
                    {isSubmitting ? 'Processing...' : (
                      <>
                        {paymentMethod === 'stripe' ? (
                          <>
                            <CreditCard className="h-5 w-5 mr-2" />
                            Pay Online
                          </>
                        ) : (
                          <>
                            💵 Place Order
                          </>
                        )}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>

            {/* Location Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              {/* Map */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 flex items-center">
                  <MapPin className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-red-500" />
                  Find Us
                </h3>
                <GoogleMap />
              </div>

              {/* Hours */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 flex items-center">
                  <Clock className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-red-500" />
                  Opening Hours
                </h3>
                <div className="space-y-1 text-xs sm:text-sm">
                  {loading ? (
                    <div className="text-center">Loading hours...</div>
                  ) : (
                    formatOpeningHours(openingHours).map(({ day, hours }) => (
                      <div key={day} className="flex justify-between">
                        <span>{day}:</span>
                        <span>{hours}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Stripe Checkout Modal - No longer needed, direct redirect */}
    </div>
  );
};

export default OrderPage;
